import { headerHeightMobile, headerHeight } from '@pushpay/shell';
import { multiply, createUseStyles } from '@pushpay/styles';
import { GivingOldTheme } from '@pushpay/theming';
import { overlayWidthMobile, headerFocusColor, headerHoverColor } from '../../common';
export const logoSize = '24px';
export const headerPadding = '16px';
const useStyles = createUseStyles(theme => {
  const isGivingOld = GivingOldTheme.colors['product-color'] === theme.colors['product-color']; // back compatibility for giving old(red)

  return {
    /**
     * The outer `header` style.
     */
    root: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: isGivingOld ? theme.colors['palette-red'] : theme.colors['color-background-surface'],
      color: isGivingOld ? theme.colors['palette-white'] : theme.colors['color-text-default'],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: headerHeight,
      zIndex: theme.ELEVATION.LAYOUT.Z_INDEX + 1,
      boxShadow: `0 1px 0 0 ${theme.colors['color-border-surface']}`,
      paddingRight: multiply(theme.SPACING.XSMALL, 1.5),
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        height: headerHeightMobile,
        paddingRight: theme.SPACING.XXSMALL
      },
      [theme.mediaQuery({
        mediaType: 'PRINT'
      })]: {
        display: 'none'
      }
    },

    /**
     * The style for the container on the right side of the header.
     */
    rightSide: {
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end'
    },
    logoLink: {
      height: headerHeight,
      display: 'flex',
      alignItems: 'center',
      marginLeft: headerPadding,
      color: isGivingOld ? theme.colors['palette-white'] : theme.colors['color-text-default'],
      position: 'relative',
      '&:hover, &:focus': {
        color: isGivingOld ? theme.colors['palette-white'] : theme.colors['color-text-default'],
        outline: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: theme.colors['color-border-surface']
        }
      },
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        height: headerHeightMobile
      }
    },

    /**
     * The style for the links.
     */
    links: {
      display: 'flex',
      alignItems: 'center',
      color: 'inherit',
      marginLeft: theme.SPACING.XXSMALL
    },
    linkAction: {
      width: '40px',
      height: '40px',
      justifyContent: 'center',
      borderRadius: theme.SHAPE.CIRCULAR,
      '&:hover': {
        color: isGivingOld ? theme.colors['palette-white'] : theme.colors['color-text-default'],
        backgroundColor: isGivingOld ? headerHoverColor : theme.colors['palette-grey-light-300']
      },
      '&:focus': {
        outline: 'none',
        color: isGivingOld ? theme.colors['palette-white'] : theme.colors['color-text-default'],
        backgroundColor: isGivingOld ? headerFocusColor : theme.colors['palette-grey-light-300']
      }
    },

    /** The style for the Pushpay logo.
     * The style for the
     */
    pushpayLogo: {
      fontSize: '40px'
    },
    helpCenterLink: {
      '&:hover, &:focus': {
        color: isGivingOld ? theme.colors['color-text-inverse'] : theme.colors['color-text-default']
      }
    },

    /**
     * The style for the help center logo.
     */
    helpCenter: {
      fontSize: '1.5em'
    },

    /**
     * The style for the divider.
     */
    divider: {
      marginLeft: theme.SPACING.SMALL,
      marginRight: theme.SPACING.SMALL
    },
    orgOverlayWrapper: {
      backgroundColor: theme.colors['color-border-surface']
    },
    overlay: {
      padding: `${headerPadding} 0`,
      minWidth: '212px',
      right: 0,
      left: 'auto'
    },
    overlayHeader: {
      color: theme.colors['color-text-heading'],
      font: theme.TYPOGRAPHY.HEADLINE_4,
      margin: `0 ${headerPadding} ${headerPadding} ${headerPadding}`,
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        padding: `${multiply(headerPadding, 2)} ${multiply(headerPadding, 2)} ${headerPadding}`,
        margin: 0
      }
    },
    options: {
      maxHeight: '220px',
      overflowY: 'auto',
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        paddingBottom: headerPadding
      }
    },
    orgSwitcher: {
      alignSelf: 'stretch',
      display: 'flex'
    },
    orgLine: {
      display: 'flex',
      alignItems: 'center',
      color: theme.colors['color-text-default'],
      padding: `${theme.SPACING.GUTTER} ${headerPadding}`,
      '&:hover, &:focus': {
        color: theme.colors['palette-white'],
        backgroundColor: theme.colors['palette-grey-dark-200']
      },
      '&, &:focus, &:hover': {
        textDecoration: 'none'
      },
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        padding: `${headerPadding} ${multiply(headerPadding, 2)}`
      }
    },
    orgLogo: {
      width: logoSize,
      height: logoSize,
      marginRight: theme.SPACING.GUTTER
    },
    orgName: {
      minWidth: '100px',
      maxWidth: '250px',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        maxWidth: '100%'
      }
    },
    arrow: {
      fontSize: '10px',
      marginLeft: theme.SPACING.GUTTER
    },
    mobileOverlay: {
      width: overlayWidthMobile
    },
    orgSummary: {
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        flex: '1 1 auto',
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    orgLabel: {
      font: theme.TYPOGRAPHY.BODY_1,
      display: 'block',
      maxWidth: '50vw',
      padding: `${theme.SPACING.GUTTER} 0`,
      textAlign: 'right',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden'
    },
    orgButton: {
      padding: `0 ${theme.SPACING.XXSMALL} 0 ${theme.SPACING.SMALL}`,
      borderRadius: isGivingOld ? '500px' : theme.SHAPE.ROUNDED_CORNERS,
      '&:hover': {
        backgroundColor: isGivingOld ? headerHoverColor : theme.colors['palette-grey-light-300']
      },
      '&:focus': {
        backgroundColor: isGivingOld ? headerFocusColor : theme.colors['palette-grey-light-300']
      },
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        padding: `0 ${theme.SPACING.SMALL}`
      }
    },
    capHeader: {
      [theme.mediaBreakpoint('TABLET_AND_BELOW')]: {
        paddingLeft: '50px',
        height: headerHeightMobile
      }
    },
    leftSectionMobile: {
      marginLeft: '56px'
    },
    personalAccountIcon: {
      width: theme.SPACING.LARGE_ICON_SIZE,
      height: theme.SPACING.LARGE_ICON_SIZE
    }
  };
});
export default useStyles;